/**
 * Per-case investigations store.
 *
 * Holds the saved Investigation collection (synced from backend), an
 * `activeId` selector, and a single `transient` slot for assistant-pushed
 * views. The Graph Views route is the sole consumer; the agent sidebar pushes
 * into `setTransient` from outside the React subtree via the module-level
 * cache (mirrors the filter-store pattern).
 *
 * `transient` is a single slot, not a queue: a second assistant push
 * replaces the first. Anything not yet saved is by definition expendable.
 */

import { createStore, type StoreApi } from 'zustand/vanilla'
import { withProperties } from '@/features/analyst-facts/fact-marks'
import { snippetNodeId, upsertSourceSnippet } from '@/features/source-snippets/object-set'
import { graphApi, type Relationship } from '@/services/api/graph'
import { investigationsApi } from '@/services/api/investigations'
import type { GraphView, GraphViewEdge } from '@/types/graph-view'
import type { Investigation, InvestigationPatchBody } from '@/types/investigation'
import type { SourceSnippet } from '@/types/source-snippet'
import { mergeObjectSet } from './merge-object-set'

export interface TransientView {
  source: 'assistant' | 'insight'
  graphView: GraphView
  suggestedTitle: string
  /** When the transient came from an insight card, its id — used by the strip
   * to highlight "you came from here" while the transient is active. */
  fromInsightId?: string
}

export interface InvestigationsState {
  caseId: string
  saved: Investigation[]
  activeId: string | null
  transient: TransientView | null
  /**
   * True when the currently-active saved investigation has been edited since
   * last save (or load). Resets on save / setActive / remove. Ignored when
   * there is no active saved investigation (transient or empty modes).
   */
  dirty: boolean
  /**
   * Snapshot of `entityCount` at the moment the active investigation was last
   * loaded or saved — used to compute additive/subtractive deltas in the
   * Modified status indicator.
   */
  baselineEntityCount: number | null
  baseline: Investigation | null
  isLoading: boolean
  error: Error | null

  fetchAll(): Promise<void>
  /** Fetch one investigation's full detail (graph_view + geo) and merge it
   *  into `saved`. The list omits the geo blob, so this is how the active
   *  investigation gains its precomputed map projection. */
  fetchOne(id: string): Promise<void>

  setTransient(graphView: GraphView, suggestedTitle: string, fromInsightId?: string): void
  clearTransient(): void

  setActive(id: string | null): void

  saveTransient(title?: string): Promise<Investigation | null>
  updateActive(patch: InvestigationPatchBody): Promise<void>
  /** Patch any investigation by id — used for rename / read-only / etc.
   *  on rows that aren't the currently-active one. */
  updateById(id: string, patch: InvestigationPatchBody): Promise<void>
  toggleReadOnly(id: string): Promise<void>
  remove(id: string): Promise<void>
  /** Refresh node `properties` for an investigation from current Neo4j
   * state. Brings classification stamps (`_render_mode`, `_geo_*`,
   * `_temporal_*`, `_track_*`) into snapshots saved before stamping
   * shipped, and folds the entities merged away since the save into the
   * entities they were merged into (ADR-0079). Returns
   * `{ synced, merged, missing, total }` so callers can toast. */
  syncProperties(
    id: string
  ): Promise<{ synced: number; merged: number; missing: number; total: number }>
  /** Re-synthesize summary/confidence/evidence for an investigation by
   * running the search agent against its current state. Long-running —
   * callers should show a loading spinner. */
  regenerateEvidence(id: string): Promise<Investigation>

  /** Mark the active investigation as dirty (modified-since-load). No-op when
   * there's no active saved investigation. */
  markDirty(): void
  /** Save current dirty state of active investigation (writes graphView etc.). */
  saveActive(): Promise<void>
  /** Throw away modifications and reload the active saved investigation. */
  discardChanges(): void

  /**
   * Additive merge — append the given graph view's nodes/edges to the
   * current viewpoint (transient if active, else active saved investigation,
   * else creates a new transient). Returns the contribution metadata
   * (caller can display it as a chip).
   *
   * When merging into a saved investigation, the change is held client-side
   * as a `dirty` edit until the user explicitly saves. We do NOT auto-PATCH
   * the backend so the user keeps control over what gets persisted.
   */
  addToObjectSet(
    view: GraphView,
    label: string,
    source: 'insight' | 'agent' | 'expand'
  ): {
    contributionId: string
    addedNodeIds: string[]
    addedEdgeIds: string[]
  }

  /**
   * Subtractive counterpart of `addToObjectSet`. Drops the listed node and
   * edge ids from the current viewpoint (transient or active saved). Edges
   * that reference a removed node are also dropped to avoid dangling
   * connectors. Saved investigations become `dirty` and are persisted on the
   * user's next explicit save — same model as the additive path.
   */
  removeFromObjectSet(nodeIds: readonly string[], edgeIds: readonly string[]): void
  upsertSnippet(snippet: SourceSnippet): void
  removeSnippet(id: string): void
  /**
   * Replace the properties of the node or edge `id` in the current viewpoint: an analyst's mark
   * (PRD-0075 R-C3, R-C4) changed them on the server, and the views read them from the snapshot.
   * A saved investigation becomes dirty, so a save keeps the mark in its snapshot.
   */
  patchElementProperties(id: string, properties: Record<string, unknown>): void

  /**
   * Snapshot-side counterpart of the backend entity-resolution merge.
   *
   * Mirrors what Neo4j does on a merge: drop the absorbed node, redirect
   * every edge that referenced it to the kept node, drop the POSSIBLE_MATCH
   * connecting the two, drop any self-loops or duplicate edges that fall out
   * of the redirection, and (when `mergeProperties` is true) shallow-merge
   * the absorbed node's properties into the kept node — only keys that the
   * kept node doesn't already have, skipping internal `_`-prefixed ones, to
   * match the backend's `merge_properties=true` semantics.
   *
   * Marks the active saved investigation `dirty`; the caller is responsible
   * for persisting via `saveActive()` (same convention as other mutators).
   */
  mergeNodesInSnapshot(args: {
    absorbedNodeId: string
    keptNodeId: string
    possibleMatchEdgeId: string
    mergeProperties: boolean
  }): void

  /**
   * Internal — fired off after `addToObjectSet` to pull any case-graph edges
   * that exist between the current view's nodes but weren't in the source
   * GraphView. Without this, a finding card that adds Person + PhoneNumber
   * lands as two isolated nodes on the canvas even though `HAS_PHONE` exists
   * in Neo4j. Async + best-effort — silent no-op on network failure.
   */
  _backfillEdgesAfterMerge(): Promise<void>

  /**
   * Push a server-created investigation into the saved list (idempotent on id).
   * For flows that create an Investigation outside the store's own create/save
   * paths — e.g. promoting a hypothesis finding — so the detail page can find
   * it immediately on navigation without waiting for a refresh.
   */
  upsertSaved(inv: Investigation): void
}

interface CreateOptions {
  caseId: string
}

/**
 * Pure helper extracted from `mergeNodesInSnapshot`. Drops the absorbed node
 * from the list and — when `mergeProperties` is true — shallow-merges its
 * non-internal, missing keys into the kept node (matches the backend cypher
 * in case_graph.py's merge_entities endpoint).
 */
function buildMergedNodes(
  absorbedNodeId: string,
  keptNodeId: string,
  mergeProperties: boolean,
  nodes: GraphView['nodes']
): GraphView['nodes'] {
  const absorbed = nodes.find(n => n.id === absorbedNodeId)
  return nodes.flatMap(n => {
    if (n.id === absorbedNodeId) return []
    if (n.id !== keptNodeId) return [n]
    if (!mergeProperties || !absorbed) return [n]
    const merged: Record<string, unknown> = { ...n.properties }
    for (const [k, v] of Object.entries(absorbed.properties ?? {})) {
      if (k.startsWith('_')) continue
      if (k in merged) continue
      merged[k] = v
    }
    return [{ ...n, properties: merged }]
  })
}

/**
 * Pure helper extracted from `mergeNodesInSnapshot`. Redirects every edge
 * referencing the absorbed node onto the kept node, drops the POSSIBLE_MATCH
 * connecting them, drops direct absorbed↔kept edges (would become self-loops;
 * the backend doesn't transfer those either — see `WHERE other <> target` in
 * the merge cypher), drops any post-redirect self-loops, and dedupes by id.
 */
function redirectAndDedupEdges(
  absorbedNodeId: string,
  keptNodeId: string,
  possibleMatchEdgeId: string,
  edges: GraphView['edges']
): GraphView['edges'] {
  const seenEdgeIds = new Set<string>()
  const nextEdges: GraphView['edges'] = []
  for (const e of edges) {
    if (e.id === possibleMatchEdgeId) continue
    const touchesAbsorbed = e.source === absorbedNodeId || e.target === absorbedNodeId
    const touchesKept = e.source === keptNodeId || e.target === keptNodeId
    if (touchesAbsorbed && touchesKept) continue
    const redirected = touchesAbsorbed
      ? {
          ...e,
          source: e.source === absorbedNodeId ? keptNodeId : e.source,
          target: e.target === absorbedNodeId ? keptNodeId : e.target,
        }
      : e
    if (redirected.source === redirected.target) continue
    if (seenEdgeIds.has(redirected.id)) continue
    seenEdgeIds.add(redirected.id)
    nextEdges.push(redirected)
  }
  return nextEdges
}

const STORE_CACHE = new Map<string, StoreApi<InvestigationsState>>()

export function getOrCreateInvestigationsStore(opts: CreateOptions): StoreApi<InvestigationsState> {
  const existing = STORE_CACHE.get(opts.caseId)
  if (existing) return existing
  const created = createInvestigationsStore(opts)
  STORE_CACHE.set(opts.caseId, created)
  return created
}

export function createInvestigationsStore({
  caseId,
}: CreateOptions): StoreApi<InvestigationsState> {
  return createStore<InvestigationsState>((set, get) => ({
    caseId,
    saved: [],
    activeId: null,
    transient: null,
    dirty: false,
    baselineEntityCount: null,
    baseline: null,
    isLoading: false,
    error: null,

    async fetchAll() {
      set({ isLoading: true, error: null })
      try {
        const saved = await investigationsApi.list(caseId)
        set(state => ({
          saved: saved.map(item => {
            const current = state.saved.find(existing => existing.id === item.id)
            return current && item.id === state.activeId && (state.dirty || current.geo)
              ? current
              : item
          }),
          isLoading: false,
        }))
      } catch (err) {
        set({ isLoading: false, error: err as Error })
      }
    },

    async fetchOne(id) {
      try {
        const full = await investigationsApi.get(caseId, id)
        set(state => ({
          baseline: state.activeId === id ? full : state.baseline,
          // Replace the (geo-less) list entry with the full detail, or append
          // it if the list hasn't loaded yet. Don't clobber unsaved local edits
          // to the active investigation's graph_view.
          saved: state.saved.some(i => i.id === id)
            ? state.saved.map(i =>
                i.id === id
                  ? state.dirty && state.activeId === id
                    ? { ...i, geo: full.geo }
                    : full
                  : i
              )
            : [full, ...state.saved],
        }))
      } catch {
        // Non-fatal: the map falls back to the live /geo endpoint, and the
        // list entry (graph_view) is enough for everything else.
      }
    },

    setTransient(graphView, suggestedTitle, fromInsightId) {
      set({
        transient: {
          source: fromInsightId ? 'insight' : 'assistant',
          graphView,
          suggestedTitle,
          fromInsightId,
        },
        // Selecting transient implicitly: clear active so the canvas reads
        // from `transient` instead of a saved investigation.
        activeId: null,
        dirty: false,
        baselineEntityCount: graphView.nodes?.length ?? null,
        baseline: null,
      })
    },

    clearTransient() {
      set({ transient: null, dirty: false, baselineEntityCount: null, baseline: null })
    },

    setActive(id) {
      const baselineEntityCount = id
        ? (get().saved.find(i => i.id === id)?.graphView?.nodes?.length ?? null)
        : null
      set({
        activeId: id,
        transient: id ? null : get().transient,
        dirty: false,
        baselineEntityCount,
        baseline: id ? (get().saved.find(i => i.id === id) ?? null) : null,
      })
      // Pull the active investigation's full detail (graph_view + the geo
      // projection the list omits) once, unless we already have its geo.
      if (id && !get().saved.find(i => i.id === id)?.geo) {
        void get().fetchOne(id)
      }
    },

    async saveTransient(title) {
      const t = get().transient
      if (!t) return null
      const finalTitle = (title ?? t.suggestedTitle).trim() || 'Untitled investigation'
      const created = await investigationsApi.create(caseId, {
        title: finalTitle,
        source: 'ai',
        graphView: t.graphView,
      })
      set(state => {
        const current = state.transient
        const sameDraft = current?.graphView === t.graphView
        // A view switch during the request must not activate the saved copy.
        if (!current || state.activeId) return { saved: [created, ...state.saved] }
        return {
          saved: [
            sameDraft ? created : { ...created, graphView: current.graphView },
            ...state.saved,
          ],
          activeId: created.id,
          transient: null,
          dirty: !sameDraft,
          baselineEntityCount: created.graphView?.nodes?.length ?? null,
          baseline: created,
        }
      })
      return created
    },

    async updateActive(patch) {
      const id = get().activeId
      if (!id) return
      await get().updateById(id, patch)
    },

    async updateById(id, patch) {
      const updated = await investigationsApi.update(caseId, id, patch)
      set(state => ({
        saved: state.saved.map(inv => {
          if (inv.id !== id) return inv
          return state.activeId === id && state.dirty && !patch.graphView
            ? {
                ...updated,
                graphView: inv.graphView,
                notes: patch.notes ? updated.notes : inv.notes,
              }
            : updated
        }),
        baseline: state.activeId === id ? updated : state.baseline,
      }))
    },

    patchElementProperties(id, properties) {
      const state = get()
      if (state.transient) {
        const next = withProperties(state.transient.graphView, id, properties)
        if (next) set({ transient: { ...state.transient, graphView: next } })
        return
      }
      const active = state.saved.find(item => item.id === state.activeId)
      const next = active ? withProperties(active.graphView, id, properties) : null
      if (!active || !next) return
      set(s => ({
        saved: s.saved.map(item => (item.id === active.id ? { ...item, graphView: next } : item)),
        dirty: true,
      }))
    },

    upsertSaved(inv) {
      set(state => ({
        saved: state.saved.some(i => i.id === inv.id)
          ? state.saved.map(i => (i.id === inv.id ? inv : i))
          : [inv, ...state.saved],
      }))
    },

    async toggleReadOnly(id) {
      const current = get().saved.find(inv => inv.id === id)
      if (!current) return
      await get().updateById(id, { readOnly: !current.readOnly })
    },

    async remove(id) {
      await investigationsApi.remove(caseId, id)
      set(state => {
        const wasActive = state.activeId === id
        return {
          saved: state.saved.filter(inv => inv.id !== id),
          activeId: wasActive ? null : state.activeId,
          dirty: wasActive ? false : state.dirty,
          baselineEntityCount: wasActive ? null : state.baselineEntityCount,
        }
      })
    },

    async syncProperties(id) {
      const { investigation, ...counts } = await investigationsApi.syncProperties(caseId, id)
      set(state => ({
        saved: state.saved.map(inv => (inv.id === id ? investigation : inv)),
        // The synced snapshot is what a later "Discard" falls back to.
        baseline: state.activeId === id ? investigation : state.baseline,
        baselineEntityCount:
          state.activeId === id ? investigation.graphView.nodes.length : state.baselineEntityCount,
      }))
      return counts
    },

    async regenerateEvidence(id) {
      const refreshed = await investigationsApi.regenerateEvidence(caseId, id)
      set(state => ({
        saved: state.saved.map(inv => (inv.id === id ? refreshed : inv)),
      }))
      return refreshed
    },

    markDirty() {
      const id = get().activeId
      if (!id) return
      if (get().dirty) return
      set({ dirty: true })
    },

    async saveActive() {
      const id = get().activeId
      if (!id) return
      const inv = get().saved.find(i => i.id === id)
      if (!inv) return
      const updated = await investigationsApi.update(caseId, id, {
        graphView: inv.graphView,
        notes: inv.notes,
      })
      set(state => {
        const current = state.saved.find(item => item.id === id)
        const unchanged = current?.graphView === inv.graphView && current?.notes === inv.notes
        return {
          saved: state.saved.map(item => (item.id !== id ? item : unchanged ? updated : item)),
          dirty: state.activeId === id ? !unchanged : state.dirty,
          baseline: state.activeId === id ? updated : state.baseline,
          baselineEntityCount:
            state.activeId === id ? updated.graphView.nodes.length : state.baselineEntityCount,
        }
      })
    },

    discardChanges() {
      const id = get().activeId
      if (!id) return
      const baseline = get().baseline
      set(state => ({
        dirty: false,
        saved:
          baseline?.id === id
            ? state.saved.map(item => (item.id === id ? baseline : item))
            : state.saved,
      }))
      if (!baseline?.geo) void get().fetchOne(id)
    },

    upsertSnippet(snippet) {
      const state = get()
      const active = state.saved.find(item => item.id === state.activeId)
      if (active?.readOnly) throw new Error('This investigation is read-only.')
      if (snippet.snapshot.caseId !== caseId)
        throw new Error('The snippet belongs to another case.')
      const view = state.transient?.graphView ?? active?.graphView ?? null
      const next = upsertSourceSnippet(view, snippet)
      if (next.snippets && next.snippets.length > 50)
        throw new Error('An investigation supports up to 50 snippets.')
      if (state.transient) set({ transient: { ...state.transient, graphView: next } })
      else if (active)
        set({
          saved: state.saved.map(item =>
            item.id === active.id ? { ...item, graphView: next } : item
          ),
          dirty: true,
        })
      else state.setTransient(next, snippet.title)
    },

    removeSnippet(id) {
      const state = get()
      if (state.saved.find(item => item.id === state.activeId)?.readOnly)
        throw new Error('This investigation is read-only.')
      state.removeFromObjectSet([snippetNodeId(id)], [])
    },

    addToObjectSet(view, label, _source) {
      const state = get()
      const active = state.saved.find(item => item.id === state.activeId)
      if (!state.transient && active?.readOnly) throw new Error('This investigation is read-only.')
      if (view.snippets?.some(snippet => snippet.snapshot.caseId !== caseId))
        throw new Error('The snippet belongs to another case.')
      const base = state.transient?.graphView ?? active?.graphView ?? null
      const merged = mergeObjectSet(base, view)
      const { addedNodeIds, addedEdgeIds } = merged
      const revealsFace = view.view.expandedFaceIds?.some(
        id => !base?.view.expandedFaceIds?.includes(id)
      )
      if (!addedNodeIds.length && !addedEdgeIds.length && !revealsFace)
        return { contributionId: '', addedNodeIds, addedEdgeIds }
      if (state.transient) {
        const transient = state.transient
        state.setTransient(merged.view, transient.suggestedTitle, transient.fromInsightId)
      } else if (active) {
        set(s => ({
          saved: s.saved.map(item =>
            item.id === active.id ? { ...item, graphView: merged.view } : item
          ),
          dirty: true,
        }))
      } else {
        state.setTransient(merged.view, label)
      }
      void get()._backfillEdgesAfterMerge()
      return { contributionId: `c-${Date.now()}`, addedNodeIds, addedEdgeIds }
    },

    async _backfillEdgesAfterMerge() {
      const state = get()
      const view: GraphView | null = state.transient
        ? state.transient.graphView
        : state.activeId
          ? (state.saved.find(i => i.id === state.activeId)?.graphView ?? null)
          : null
      if (!view || view.nodes.length < 2) return

      const nodeIds = view.nodes.map(n => n.id)

      let fetched: Relationship[] = []
      try {
        const res = await graphApi.getEdgesBetween(state.caseId, nodeIds)
        fetched = res.relationships ?? []
      } catch (err) {
        console.error('[investigations] edge backfill failed', err)
        return
      }
      if (fetched.length === 0) return

      // Re-read state — the user may have added more nodes or switched view
      // during the await window. Re-derive the merge target and skip if it
      // has gone away.
      const fresh = get()
      const target: GraphView | null = fresh.transient
        ? fresh.transient.graphView
        : fresh.activeId
          ? (fresh.saved.find(i => i.id === fresh.activeId)?.graphView ?? null)
          : null
      if (!target) return

      const existingEdgeIds = new Set(target.edges.map(e => e.id))
      const currentNodeIds = new Set(target.nodes.map(n => n.id))
      const newEdges: GraphViewEdge[] = fetched
        .filter(e => !existingEdgeIds.has(e.id))
        .filter(e => currentNodeIds.has(e.source) && currentNodeIds.has(e.target))
        .map(e => ({
          id: e.id,
          type: e.type ?? '',
          source: e.source,
          target: e.target,
          properties: e.properties ?? {},
        }))
      if (newEdges.length === 0) return

      const merged: GraphView = {
        ...target,
        edges: [...target.edges, ...newEdges],
      }

      if (fresh.transient) {
        const t = fresh.transient
        fresh.setTransient(merged, t.suggestedTitle, t.fromInsightId)
      } else if (fresh.activeId) {
        const aid = fresh.activeId
        set(s => ({
          saved: s.saved.map(i => (i.id === aid ? { ...i, graphView: merged } : i)),
          dirty: true,
        }))
      }
    },

    removeFromObjectSet(nodeIds, edgeIds) {
      const state = get()
      if (nodeIds.length === 0 && edgeIds.length === 0) return
      const dropNodes = new Set(nodeIds)
      const dropEdges = new Set(edgeIds)

      const prune = (gv: GraphView): GraphView => {
        const nextNodes = gv.nodes.filter(n => !dropNodes.has(n.id))
        const nextEdges = gv.edges.filter(
          e => !dropEdges.has(e.id) && !dropNodes.has(e.source) && !dropNodes.has(e.target)
        )
        const snippets = gv.snippets?.filter(item => !dropNodes.has(snippetNodeId(item.id)))
        return { ...gv, nodes: nextNodes, edges: nextEdges, snippets }
      }

      if (state.transient) {
        const t = state.transient
        const next = prune(t.graphView)
        if (
          next.nodes.length === t.graphView.nodes.length &&
          next.edges.length === t.graphView.edges.length &&
          next.snippets?.length === t.graphView.snippets?.length
        )
          return
        set({
          transient: { ...t, graphView: next },
          baselineEntityCount: next.nodes.length,
        })
        return
      }

      if (state.activeId) {
        const active = state.saved.find(i => i.id === state.activeId)
        if (!active) return
        const next = prune(active.graphView)
        if (
          next.nodes.length === active.graphView.nodes.length &&
          next.edges.length === active.graphView.edges.length &&
          next.snippets?.length === active.graphView.snippets?.length
        )
          return
        set(s => ({
          saved: s.saved.map(i => (i.id === active.id ? { ...i, graphView: next } : i)),
          dirty: true,
        }))
      }
    },

    mergeNodesInSnapshot({ absorbedNodeId, keptNodeId, possibleMatchEdgeId, mergeProperties }) {
      // Thin coordinator over the two pure helpers above. The early bail-out
      // ensures we don't spend any work when neither endpoint is in view.
      const apply = (gv: GraphView): GraphView => {
        const hasAbsorbed = gv.nodes.some(n => n.id === absorbedNodeId)
        const hasKept = gv.nodes.some(n => n.id === keptNodeId)
        // Bail out when *either* endpoint is missing from the snapshot — if
        // only one side is present, redirectAndDedupEdges would rewire edges
        // to a non-existent node id, leaving dangling references.
        if (!hasAbsorbed || !hasKept) return gv
        const nextNodes = buildMergedNodes(absorbedNodeId, keptNodeId, mergeProperties, gv.nodes)
        const nextEdges = redirectAndDedupEdges(
          absorbedNodeId,
          keptNodeId,
          possibleMatchEdgeId,
          gv.edges
        )
        return { ...gv, nodes: nextNodes, edges: nextEdges }
      }

      // Write the pruned snapshot back to whichever slot is active —
      // transient takes precedence over the saved investigation.
      const applyToState = () => {
        const state = get()
        if (state.transient) {
          const t = state.transient
          const next = apply(t.graphView)
          set({
            transient: { ...t, graphView: next },
            baselineEntityCount: next.nodes.length,
          })
          return
        }
        if (state.activeId) {
          const active = state.saved.find(i => i.id === state.activeId)
          if (!active) return
          const next = apply(active.graphView)
          set(s => ({
            saved: s.saved.map(i => (i.id === active.id ? { ...i, graphView: next } : i)),
            dirty: true,
          }))
        }
      }

      applyToState()
    },
  }))
}
